import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    useTheme,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    Divider,
    Link,
    Chip
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Close as CloseIcon,
    AccessTime,
    VideoCall,
    CalendarToday,
    Cancel as CancelIcon,
    Topic as TopicIcon
} from '@mui/icons-material';
import moment from 'moment';
import ViewEventDialog from './ViewEventDialog';
import ManageEventDialog from './ManageEventDialog';
import { serviceOptions, UserTypeId } from '../../common/const';
import CommonBookSession from '../../common/components/CommonBookSession';
import { useSelector } from 'react-redux';

// Event type colors matching your image
const EVENT_TYPE_COLORS = {
    'HIGH': '#FF4D4F', // red
    'MEDIUM': '#FAAD14', // yellow
    'LOW': '#52C41A', // green
};

const EVENT_TYPE_LABELS = {
    'HIGH': 'High Priority',
    'MEDIUM': 'Medium Priority',
    'LOW': 'Low Priority'
};

// Session type colors
const SESSION_TYPE_COLORS = {
    'SAT_ACT_COACHING': '#2196F3', // Blue
    'AP_TUTORING': '#9C27B0', // Purple
    'COUNSELING': '#FF9800', // Orange
    'ACADEMIC_TUTORING': '#4CAF50', // Green
    'CODING_SKILLS_TECHNOLOGY': '#F44336', // Red
    'default': '#757575' // Grey
};

const SESSION_TYPE_LABELS = {
    'SAT_ACT_COACHING': 'SAT/ACT Coaching',
    'AP_TUTORING': 'AP Tutoring',
    'COUNSELING': 'Counselling',
    'ACADEMIC_TUTORING': 'Academic Tutoring',
    'CODING_SKILLS_TECHNOLOGY': 'Coding & Technology'
};

// Session Popup Component
const SessionPopup = ({ open, data, onClose, setSelectedSesion, setOpen }) => {
    const { user } = useSelector((state) => state.auth);
    if (!data || !data.session) return null;

    const { session, avatar } = data;

    const canJoinMeeting = (session) => {
        if (!session.meeting_link) return false;
        const now = moment();
        const start = moment(session.session_start_time);
        const end = moment(session.session_end_time);
        return now.isBetween(start, end);
    };

    const getStatusChip = (session) => {
        const now = moment();
        const start = moment(session.session_start_time);
        const end = moment(session.session_end_time);

        if (now.isAfter(end)) {
            return <Chip label="Completed" size="small" color="default" />;
        } else if (now.isBetween(start, end)) {
            return <Chip label="In Progress" size="small" color="success" />;
        } else if (now.isBefore(start)) {
            return <Chip label="Upcoming" size="small" color="info" />;
        } else if (session.status === 'CANCELLED') {
            return <Chip label="Cancelled" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#ffebee', color: '#f44336' }} />;
        }
        return <Chip label={session.status} size="small" color="warning" />;
    };

    const studentName = `${session?.student?.user?.first_name || ''} ${session?.student?.user?.last_name || ''}`;
    const studentInitial = session?.student?.user?.first_name?.[0] || '?';
    const canJoin = canJoinMeeting(session);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    p: 1
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Session Details</Typography>
                    {getStatusChip(session)}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Student Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                        src={avatar}
                        sx={{
                            width: 56,
                            height: 56,
                            bgcolor: SESSION_TYPE_COLORS[session.service_name] || SESSION_TYPE_COLORS.default,
                            fontSize: '24px'
                        }}
                    >
                        {!avatar && studentInitial}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {studentName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Student
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Session Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Topic */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TopicIcon sx={{ color: '#666', mt: 0.3 }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">Topic</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {session.topic || SESSION_TYPE_LABELS[session.service_name] || session.service_name?.replace(/_/g, ' ')}
                            </Typography>
                            {session.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {session.notes}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Date & Time */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <AccessTime sx={{ color: '#666', mt: 0.3 }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {moment(session.session_start_time).format('dddd, MMMM D, YYYY')}
                            </Typography>
                            <Typography variant="body2">
                                {moment(session.session_start_time).format('hh:mm A')} -
                                {moment(session.session_end_time).format('hh:mm A')}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Meeting Link */}
                    {session.meeting_link && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <VideoCall sx={{ color: '#666', mt: 0.3 }} />
                            <Box>
                                <Typography variant="body2" color="text.secondary">Meeting Link</Typography>
                                <Link
                                    href={session.meeting_link}
                                    target="_blank"
                                    rel="noopener"
                                    sx={{
                                        display: 'inline-block',
                                        maxWidth: '300px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {session.meeting_link}
                                </Link>
                            </Box>
                        </Box>
                    )}

                    {/* Service Type */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '4px',
                            bgcolor: SESSION_TYPE_COLORS[session.service_name] || SESSION_TYPE_COLORS.default,
                            mt: 0.3
                        }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">Service Type</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {SESSION_TYPE_LABELS[session.service_name] || session.service_name?.replace(/_/g, ' ')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            {user?.user_type_id === UserTypeId.Student && (
            <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
                {session.status !== 'CANCELLED' && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<VideoCall />}
                        onClick={() => {
                            setSelectedSesion(session?.reschedule_url);
                            setOpen(true);
                            onClose();
                        }}
                        disabled={!canJoin || !session.meeting_link}
                        sx={{ flex: 1 }}
                    >
                        Join Meeting
                    </Button>
                )}

                {(!moment.utc().isAfter(moment.utc(session.session_end_time)) && session.status !== 'CANCELLED') && (
                    <Button
                        variant="outlined"
                        startIcon={<CalendarToday />}
                        onClick={() => {
                            setSelectedSesion(session?.cancel_url);
                            setOpen(true);
                            onClose();
                        }}
                        sx={{ flex: 1 }}
                    >
                        Reschedule
                    </Button>
                )}

                {(!moment.utc().isAfter(moment.utc(session.session_end_time)) && session.status !== 'CANCELLED') && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                            onClose();
                        }}
                        sx={{ flex: 1 }}
                    >
                        Cancel
                    </Button>
                )}
            </DialogActions>)}
        </Dialog>
    );
};

// Helper functions
const formatDate = (date) => {
    return date?.toISOString().split('T')[0];
};

const getAllTimeSlots = () => {
    const slots = [];
    // All time slots from 12 AM to 11 PM (24 hours)
    for (let hour = 0; hour < 24; hour++) {
        const displayHour = hour % 12 === 0 ? 12 : hour % 12;
        const period = hour >= 12 ? 'PM' : 'AM';
        slots.push({
            display: `${displayHour} ${period}`,
            hour: hour,
            minutes: 0
        });
    }
    return slots;
};

const generateCalendar = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startingDay = firstDay.getDay();
    startingDay = startingDay === 0 ? 6 : startingDay - 1; // Monday start

    const days = [];
    const prevMonthDays = new Date(year, month, 0).getDate();

    // Previous month days
    for (let i = 0; i < startingDay; i++) {
        const dayDate = new Date(year, month - 1, prevMonthDays - startingDay + i + 1);
        days.push({
            date: dayDate,
            currentMonth: false,
            day: prevMonthDays - startingDay + i + 1,
            formattedDate: formatDate(dayDate),
            isToday: false,
            isDisabled: true
        });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
        const dayDate = new Date(year, month, i);
        days.push({
            date: dayDate,
            currentMonth: true,
            day: i,
            formattedDate: formatDate(dayDate),
            isToday: dayDate.toDateString() === new Date().toDateString(),
            isDisabled: false
        });
    }

    // Next month days
    const totalCells = 42; // 6 weeks * 7 days
    const remaining = totalCells - days.length;
    for (let i = 1; i <= remaining; i++) {
        const dayDate = new Date(year, month + 1, i);
        days.push({
            date: dayDate,
            currentMonth: false,
            day: i,
            formattedDate: formatDate(dayDate),
            isToday: false,
            isDisabled: true
        });
    }

    return days;
};

const DynamicCalendar = ({
    currentDate,
    setCurrentDate,
    selectedDate,
    setSelectedDate,
    viewType,
    setViewType,
    weekDates,
    events = [], // Default to empty array
    getEvents,
    getAllEvents
}) => {
    const theme = useTheme();
    const [viewOpen, setViewOpen] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [manageOpen, setManageOpen] = useState(false);
    const [sessionOpen, setSessionOpen] = useState(false);
    const [selectedSession, setSelectedSesion] = useState(null);
    const [open, setOpen] = useState(false);

    // Refs for main container scrolling
    const dayContainerRef = useRef(null);
    const weekContainerRef = useRef(null);

    const timeSlots = useMemo(() => getAllTimeSlots(), []);
    const calendarDays = useMemo(() => generateCalendar(currentDate), [currentDate]);
    const weeks = useMemo(() => {
        const weeksArray = [];
        for (let i = 0; i < calendarDays.length; i += 7) {
            weeksArray.push(calendarDays.slice(i, i + 7));
        }
        return weeksArray;
    }, [calendarDays]);

    // UPDATED: Convert UTC event timestamp to local timezone for date comparison
    const getEventsForDate = (date) => {
        const dateStr = formatDate(date);
        return events.filter(event => {
            if (!event.event_start_timestamp) return false;
            // Convert UTC to local time before comparing dates
            const eventDate = moment.utc(event.event_start_timestamp).local().format('YYYY-MM-DD');
            return eventDate === dateStr;
        });
    };

    // UNCHANGED: Get sessions for a specific date
    const getSessionsForDate = (date) => {
        const dateStr = formatDate(date);
        return events.filter(session => {
            if (!session.session_start_time) return false;
            const sessionDate = moment(session.session_start_time).format('YYYY-MM-DD');
            return sessionDate === dateStr;
        });
    };

    // Generate week dates for week view
    const getWeekDates = (date) => {
        const startOfWeek = moment(date).startOf('isoWeek'); // Monday start
        const weekDates = [];

        for (let i = 0; i < 7; i++) {
            const day = moment(startOfWeek).add(i, 'days');
            weekDates.push({
                date: day.toDate(),
                day: day.date(),
                month: day.month(),
                year: day.year(),
                dayName: day.format('ddd'),
                formattedDate: day.format('YYYY-MM-DD'),
                isToday: day.isSame(new Date(), 'day'),
                isDisabled: false
            });
        }

        return weekDates;
    };

    // Use provided weekDates or generate from currentDate
    const displayWeekDates = weekDates || getWeekDates(currentDate);

    // Navigation handlers
    const handlePrev = () => {
        const newDate = new Date(currentDate);
        if (viewType === 'day') {
            newDate.setDate(newDate.getDate() - 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        } else if (viewType === 'week') {
            newDate.setDate(newDate.getDate() - 7);
            setCurrentDate(newDate);
        } else {
            newDate.setMonth(newDate.getMonth() - 1);
            setCurrentDate(newDate);
        }
    };

    const handleNext = () => {
        const newDate = new Date(currentDate);
        if (viewType === 'day') {
            newDate.setDate(newDate.getDate() + 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        } else if (viewType === 'week') {
            newDate.setDate(newDate.getDate() + 7);
            setCurrentDate(newDate);
        } else {
            newDate.setMonth(newDate.getMonth() + 1);
            setCurrentDate(newDate);
        }
    };

    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setSelectedDate(today);
    };

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setViewType(newView);
        }
    };

    // Get header text based on view type
    const getHeaderText = () => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];

        if (viewType === 'day') {
            return selectedDate.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        } else if (viewType === 'week') {
            const start = displayWeekDates[0];
            const end = displayWeekDates[6];
            return `${monthNames[start.month]} ${start.day} - ${monthNames[end.month]} ${end.day}, ${start.year}`;
        } else {
            return `${monthNames[currentDate.getMonth()]}, ${currentDate.getFullYear()}`;
        }
    };

    // UPDATED: Render Day View — events converted from UTC to local time
    const renderDayView = () => {
        const dayEvents = getEventsForDate(selectedDate);
        const daySessions = getSessionsForDate(selectedDate);
        const totalHeight = timeSlots.length * 60;
        return (
            <Box
                ref={dayContainerRef}
                sx={{
                    height: '600px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    overflow: 'auto',
                    display: 'flex',
                    position: 'relative',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                    }
                }}
            >
                {/* Fixed Time Column */}
                <Box sx={{
                    width: 80,
                    bgcolor: '#fafafa',
                    flexShrink: 0,
                    position: 'sticky',
                    left: 0,
                    zIndex: 3
                }}>
                    {timeSlots.map((slot, index) => (
                        <Box
                            key={index}
                            sx={{
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                pr: 2,
                                borderBottom: `2px dashed ${theme.palette.divider}`
                            }}
                        >
                            <Typography fontWeight={600} variant="body2" color={theme.palette.background.dark}>
                                {slot.display}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Schedule Area */}
                <Box sx={{
                    flex: 1,
                    position: 'relative',
                    minWidth: '400px',
                    minHeight: `${totalHeight}px`,
                    borderLeft: `2px dashed ${theme.palette.divider}`,
                }}>
                    {/* Time grid lines */}
                    {timeSlots.map((slot, index) => (
                        <Box
                            key={index}
                            sx={{
                                height: 60,
                                borderBottom: `2px dashed ${theme.palette.divider}`,
                                position: 'absolute',
                                top: `${index * 60}px`,
                                left: 0,
                                right: 0
                            }}
                        />
                    ))}

                    {/* UPDATED: Events — UTC timestamp converted to local time for correct positioning */}
                    {dayEvents.map(event => {
                        // Convert UTC to local timezone before extracting hour/minute
                        const eventTime = moment.utc(event.event_start_timestamp).local();
                        const hour = eventTime.hours();
                        const minute = eventTime.minutes();
                        const startPosition = hour * 60 + minute;

                        return (
                            <Box
                                key={event.event_id}
                                sx={{
                                    position: 'absolute',
                                    left: 8,
                                    right: 8,
                                    top: `${startPosition}px`,
                                    height: '40px',
                                    bgcolor: EVENT_TYPE_COLORS[event.priority],
                                    color: 'white',
                                    p: 1,
                                    borderRadius: '4px',
                                    zIndex: 2,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        opacity: 0.9,
                                        transform: 'scale(1.02)',
                                        transition: 'all 0.2s ease'
                                    }
                                }}
                                onClick={() => {
                                    setManageOpen(true);
                                    setViewData(event);
                                }}
                            >
                                <Typography variant="body2" fontWeight="bold">
                                    {event.event_title}
                                    {' - '}
                                    {eventTime.format('hh:mm A')}
                                </Typography>
                            </Box>
                        );
                    })}

                    {/* UNCHANGED: Session rendering */}
                    {daySessions.map(session => {
                        const sessionTime = moment(session.session_start_time);
                        const hour = sessionTime.hours();
                        const minute = sessionTime.minutes();
                        const startPosition = hour * 60 + minute;

                        return (
                            <Box
                                key={session.mentor_session_id}
                                sx={{
                                    position: 'absolute',
                                    left: 8,
                                    right: 8,
                                    top: `${startPosition}px`,
                                    height: '40px',
                                    bgcolor: SESSION_TYPE_COLORS[session.service_name] || SESSION_TYPE_COLORS.default,
                                    color: 'white',
                                    p: 1,
                                    borderRadius: '4px',
                                    zIndex: 2,
                                    cursor: 'pointer',
                                    border: '2px solid white',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    '&:hover': {
                                        opacity: 0.9,
                                        transform: 'scale(1.02)',
                                        transition: 'all 0.2s ease'
                                    }
                                }}
                                onClick={() => {
                                    setViewData({ session: session, avatar: session?.student?.user?.avatar });
                                    setSessionOpen(true);
                                }}
                            >
                                <Typography variant="body2" fontWeight="bold" noWrap>
                                    {session.topic || SESSION_TYPE_LABELS[session.service_name] || session.service_name?.replace(/_/g, ' ')}
                                    {' - '}
                                    {sessionTime.format('hh:mm A')}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '10px' }}>
                                    {session.mentor?.user?.first_name || 'Mentor'}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // UPDATED: Render Week View — events converted from UTC to local time
    const renderWeekView = () => {
        const totalHeight = timeSlots.length * 60;
        return (
            <Box
                ref={weekContainerRef}
                sx={{
                    height: '600px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                    }
                }}
            >
                {/* Day Boxes */}
                <Grid container sx={{
                    bgcolor: '#fafafa',
                    p: 1,
                    pl: 10,
                    position: 'sticky',
                    top: 0,
                    zIndex: 3
                }}>
                    {displayWeekDates.map(date => {
                        const isToday = date.isToday;
                        return (
                            <Grid item xs key={date.formattedDate}>
                                <Box
                                    sx={{
                                        height: '80px',
                                        margin: '4px',
                                        p: 1,
                                        backgroundColor: isToday ? theme.palette.primary.main : 'white',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderRadius: '12px',
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography variant="body2" fontWeight="bold" color="text.secondary" fontSize="18px">
                                        {date.dayName}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: isToday ? 'bold' : 600,
                                            color: isToday ? '#333' : theme.palette.text.primary,
                                            fontSize: 18,
                                            mt: 0.5
                                        }}
                                    >
                                        {date.day}
                                    </Typography>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Time Grid Container */}
                <Box sx={{
                    display: 'flex',
                    minHeight: `${totalHeight}px`,
                    minWidth: '800px'
                }}>
                    {/* Fixed Time Column */}
                    <Box sx={{
                        width: 80,
                        bgcolor: '#fafafa',
                        flexShrink: 0,
                        position: 'sticky',
                        left: 0,
                        zIndex: 2
                    }}>
                        {timeSlots.map((slot, index) => (
                            <Box
                                key={index}
                                sx={{
                                    height: 60,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    pr: 2,
                                }}
                            >
                                <Typography fontWeight={600} variant="body2" color={theme.palette.background.dark}>
                                    {slot.display}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flex: 1,
                    }}>
                        {displayWeekDates.map((date, dayIndex) => {
                            const dateEvents = getEventsForDate(date.date);
                            const dateSessions = getSessionsForDate(date.date);
                            return (
                                <Box
                                    key={date.formattedDate}
                                    sx={{
                                        flex: 1,
                                        borderLeft: dayIndex > 0 ? `2px dashed ${theme.palette.divider}` : 'none',
                                        borderRight: dayIndex === 6 ? `2px dashed ${theme.palette.divider}` : 'none',
                                        position: 'relative',
                                        bgcolor: 'transparent'
                                    }}
                                >
                                    {/* Time slots grid */}
                                    {timeSlots.map((slot, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                height: 60,
                                                borderBottom: `2px dashed ${theme.palette.divider}`,
                                                position: 'absolute',
                                                top: `${index * 60}px`,
                                                left: 0,
                                                right: 0
                                            }}
                                        />
                                    ))}

                                    {/* UPDATED: Events — UTC to local conversion for correct time slot positioning */}
                                    {dateEvents.map(event => {
                                        // Convert UTC to local timezone before extracting hour/minute
                                        const eventTime = moment.utc(event.event_start_timestamp).local();
                                        const hour = eventTime.hours();
                                        const minute = eventTime.minutes();
                                        const startPosition = hour * 60 + minute;
                                        return (
                                            <Box
                                                key={event.event_id}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 4,
                                                    right: 4,
                                                    top: `${startPosition}px`,
                                                    height: '30px',
                                                    bgcolor: EVENT_TYPE_COLORS[event.priority],
                                                    color: 'white',
                                                    p: '0px 0px 0px 8px',
                                                    borderRadius: '4px',
                                                    zIndex: 1,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        opacity: 0.9,
                                                        transform: 'scale(1.02)',
                                                        transition: 'all 0.2s ease'
                                                    }
                                                }}
                                                onClick={() => {
                                                    setManageOpen(true);
                                                    setViewData(event);
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="caption" fontWeight="bold" noWrap fontSize="9px">
                                                        {event.event_title || event.topic?.substring(0, 15) ||
                                                            serviceOptions.find((a) => a.value === event.service_name)?.label?.substring(0, 15)}
                                                    </Typography>
                                                </Box>

                                                <Typography variant="caption" noWrap fontSize="7px" sx={{ display: 'block' }}>
                                                    {eventTime.format('hh:mm') || moment(event.session_start_time).format('h:mm A')}
                                                </Typography>
                                            </Box>
                                        );
                                    })}

                                    {/* UNCHANGED: Sessions */}
                                    {dateSessions.map(session => {
                                        const sessionTime = moment(session.session_start_time);
                                        const hour = sessionTime.hours();
                                        const minute = sessionTime.minutes();
                                        const startPosition = hour * 60 + minute;
                                        return (
                                            <Box
                                                key={session.mentor_session_id}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 4,
                                                    right: 4,
                                                    top: `${startPosition}px`,
                                                    height: '30px',
                                                    bgcolor: SESSION_TYPE_COLORS[session.service_name] || SESSION_TYPE_COLORS.default,
                                                    color: 'white',
                                                    p: '0px 0px 0px 8px',
                                                    borderRadius: '4px',
                                                    zIndex: 1,
                                                    cursor: 'pointer',
                                                    border: '2px solid white',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                    '&:hover': {
                                                        opacity: 0.9,
                                                        transform: 'scale(1.02)',
                                                        transition: 'all 0.2s ease'
                                                    }
                                                }}
                                                onClick={() => {
                                                    setViewData({ session: session, avatar: session?.student?.user?.avatar });
                                                    setSessionOpen(true);
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight="bold" noWrap fontSize="11px">
                                                    {session.topic || SESSION_TYPE_LABELS[session.service_name] || session.service_name?.replace(/_/g, ' ')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '10px', ml: 0.5 }}>
                                                    {sessionTime.format('hh:mm')}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        );
    };

    // UPDATED: Render Month View — events use local time for date matching
    const renderMonthView = () => {
        return (
            <Box sx={{
                border: `1px solid ${theme.palette.background.secondary}`,
                borderRadius: '8px',
                overflow: 'hidden',
                background: 'white'
            }}>
                {/* Week Days Header */}
                <Box sx={{
                    p: '16px 8px 0px 0px',
                    bgcolor: 'white',
                }}>
                    <Grid container>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
                            <Grid item xs key={index}>
                                <Typography align="center" fontWeight={600} color={theme.palette.background.dark}>
                                    {day}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Box sx={{ p: 1 }}>
                    {weeks.map((week, weekIndex) => (
                        <Grid container key={weekIndex} sx={{ mb: weekIndex < 5 ? 0.5 : 0 }}>
                            {week.map((day, dayIndex) => {
                                const isToday = day.isToday;
                                const isDisabled = day.isDisabled;

                                const dayYear = day.date.getFullYear();
                                const dayMonth = day.date.getMonth();
                                const dayDate = day.date.getDate();

                                // UPDATED: Convert UTC to local time before matching event date
                                const eventList = events.filter(event => {
                                    if (!event.event_start_timestamp) return false;
                                    const localDate = moment.utc(event.event_start_timestamp).local();
                                    return localDate.year() === dayYear &&
                                        localDate.month() === dayMonth &&
                                        localDate.date() === dayDate;
                                });

                                // UNCHANGED: Sessions filter
                                const sessionList = events.filter(session => {
                                    if (!session.session_start_time) return false;
                                    const sessionDate = new Date(session.session_start_time);
                                    return sessionDate.getUTCFullYear() === dayYear &&
                                        sessionDate.getUTCMonth() === dayMonth &&
                                        sessionDate.getUTCDate() === dayDate;
                                });

                                return (
                                    <Grid item xs={12 / 7} key={dayIndex}>
                                        <Box
                                            sx={{
                                                height: '100px',
                                                margin: '5px',
                                                p: 1,
                                                backgroundColor: isToday ? theme.palette.primary.main :
                                                    ((day.currentMonth ? 'white' : theme.palette.grey[100])),
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                cursor: isDisabled ? 'default' : 'pointer',
                                                borderRadius: '10px',
                                                border: isDisabled ? `1px solid ${theme.palette.grey[300]}` :
                                                    (eventList.length || sessionList.length) ? `1.5px solid ${theme.palette.secondary.main}` :
                                                        `1px solid ${theme.palette.divider}`,
                                                opacity: isDisabled ? 0.6 : 1,
                                            }}
                                        >
                                            {/* Day Number */}
                                            <Typography
                                                sx={{
                                                    fontWeight: day.currentMonth ? 600 : 400,
                                                    color: day.currentMonth ? theme.palette.text.primary : theme.palette.text.disabled,
                                                    fontSize: 20,
                                                }}
                                            >
                                                {day.day}
                                            </Typography>

                                            {/* Event dots */}
                                            {!isDisabled && eventList.length > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                        alignItems: 'flex-end',
                                                        mt: 'auto',
                                                        pt: 0.5
                                                    }}
                                                >
                                                    {eventList.slice(0, 4).map((event, eventIndex) => (
                                                        <Box
                                                            key={`event-${eventIndex}`}
                                                            className="event-dot"
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '50%',
                                                                bgcolor: EVENT_TYPE_COLORS[event.priority],
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    transform: 'scale(1.2)',
                                                                    transition: 'transform 0.2s'
                                                                }
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (!isDisabled) {
                                                                    setViewOpen(true);
                                                                    setViewData(event);
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {eventList.length > 4 && (
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            className="event-dot"
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (!isDisabled) {
                                                                    setViewOpen(true);
                                                                    setViewData(eventList[0]);
                                                                }
                                                            }}
                                                        >
                                                            +{eventList.length - 4}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}

                                            {/* UNCHANGED: Session squares */}
                                            {!isDisabled && sessionList.length > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                        alignItems: 'flex-end',
                                                        mt: 'auto',
                                                        pt: 0.5
                                                    }}
                                                >
                                                    {sessionList.slice(0, 4).map((session, sessionIndex) => (
                                                        <Box
                                                            key={`session-${sessionIndex}`}
                                                            className="session-square"
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '2px',
                                                                bgcolor: SESSION_TYPE_COLORS[session.service_name] || SESSION_TYPE_COLORS.default,
                                                                border: '1px solid white',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    transform: 'scale(1.2)',
                                                                    transition: 'transform 0.2s'
                                                                }
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (!isDisabled) {
                                                                    setViewData({
                                                                        session: session,
                                                                        avatar: session?.student?.user?.avatar
                                                                    });
                                                                    setSessionOpen(true);
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {sessionList.length > 4 && (
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            className="session-square"
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (!isDisabled) {
                                                                    setViewData({
                                                                        session: sessionList[0],
                                                                        avatar: sessionList[0]?.student?.user?.avatar
                                                                    });
                                                                    setSessionOpen(true);
                                                                }
                                                            }}
                                                        >
                                                            +{sessionList.length - 4}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: '16px 0px' }}>
            {/* Header - UNCHANGED */}
            {manageOpen && (
                <ManageEventDialog
                    viewOpen={manageOpen}
                    setViewOpen={setManageOpen}
                    viewData={viewData}
                    getEvents={getEvents}
                    getAllEvents={getAllEvents}
                />
            )}
            {viewOpen && (
                <ViewEventDialog
                    viewOpen={viewOpen}
                    setViewOpen={setViewOpen}
                    viewData={viewData}
                    getEvents={getEvents}
                    getAllEvents={getAllEvents}
                    eventList={events}
                />
            )}
            <CommonBookSession
                bookingUrl={selectedSession}
                open={open}
                setOpen={setOpen}
                handleClose={(data) => {
                    setOpen(false);
                }}
            />
            {sessionOpen && (
                <SessionPopup
                    open={sessionOpen}
                    data={viewData}
                    onClose={() => setSessionOpen(false)}
                    setSelectedSesion={setSelectedSesion}
                    setOpen={setOpen}
                />
            )}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
            }}>
                {/* Left: Today button first, then arrows, then header text */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Today Button FIRST */}
                    <Button
                        variant="contained"
                        onClick={handleToday}
                        size="medium"
                        sx={{
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: 'white',
                            color: '#333',
                            minWidth: '90px',
                            height: '40px',
                            fontSize: '14px',
                            boxShadow: 'none',
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                                bgcolor: 'white',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Today
                    </Button>

                    {/* Left Arrow */}
                    <IconButton
                        onClick={handlePrev}
                        size="medium"
                        sx={{
                            color: 'text.secondary',
                            p: 1
                        }}
                    >
                        <ChevronLeft fontSize="medium" />
                    </IconButton>

                    {/* Right Arrow */}
                    <IconButton
                        onClick={handleNext}
                        size="medium"
                        sx={{
                            color: 'text.secondary',
                            p: 1
                        }}
                    >
                        <ChevronRight fontSize="medium" />
                    </IconButton>

                    {/* Header Text */}
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '24px', ml: 1 }}>
                        {getHeaderText()}
                    </Typography>
                </Box>

                {/* Right: View Toggle */}
                <ToggleButtonGroup
                    value={viewType}
                    exclusive
                    onChange={handleViewChange}
                    size="medium"
                    sx={{
                        gap: 1.5,
                        '& .MuiToggleButton-root': {
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '15px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            height: '40px',
                            '&.Mui-selected': {
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.secondary.main,
                                borderColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main
                                }
                            },
                            '&:not(.Mui-selected)': {
                                backgroundColor: theme.palette.background.secondary,
                                color: theme.palette.background.dark,
                                '&:hover': {
                                    backgroundColor: theme.palette.background.secondary,
                                },
                                border: 'none'
                            },
                        }
                    }}
                >
                    <ToggleButton value="day">Daily</ToggleButton>
                    <ToggleButton value="week">Weekly</ToggleButton>
                    <ToggleButton value="month">Monthly</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Main Calendar Area */}
            <Box sx={{ minHeight: '600px', mb: 2 }}>
                {viewType === 'day' && renderDayView()}
                {viewType === 'week' && renderWeekView()}
                {viewType === 'month' && renderMonthView()}
            </Box>

            {/* Legend - Combined Events and Sessions */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 4,
                flexWrap: 'wrap',
                mb: 2
            }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>Events:</Typography>
                    {Object.entries(EVENT_TYPE_LABELS).map(([type, label]) => (
                        <Box key={type} display="flex" gap={1} alignItems="center">
                            <Box sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: EVENT_TYPE_COLORS[type]
                            }} />
                            <Typography variant="body2" fontWeight={500}>
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>Sessions:</Typography>
                    {Object.entries(SESSION_TYPE_LABELS).map(([type, label]) => (
                        <Box key={type} display="flex" gap={1} alignItems="center">
                            <Box sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '2px',
                                bgcolor: SESSION_TYPE_COLORS[type]
                            }} />
                            <Typography variant="body2" fontWeight={500}>
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default DynamicCalendar;